import { FieldMergeFunction, FieldPolicy, InMemoryCacheConfig, Reference } from '@apollo/client';

import { appointmentDaysTypePolicy } from '~/hooks/appointment-days';

interface CacheEdge {
    node?: Reference & { id?: string };
}

/**
 * Keeps the fields that different queries have read into the same cached object. Used
 * for the top-level `root` field further down: every query of the member API reads its
 * data through `root` but selects only a few of its fields, and without this merge the
 * newest result would replace the whole cached object and delete the fields the other
 * queries had written. The chat subscription also relies on it: the cache update in
 * src/hooks/chat/chat.ts writes a `root` that contains nothing but the chat, and the
 * merge is what keeps the rest of `root` alive. So this must not be removed.
 *
 * One thing to know when writing test data: mergeObjects merges only as long as both
 * objects claim the same GraphQL type. If they claim a different type, it replaces the
 * cached object instead, which brings back exactly the data loss described above. The
 * real API cannot get this wrong (`root` is always a MemberRootNode), but a mock can:
 * a fixture with a wrong `__typename` deletes the cached fields of the other queries in
 * the same test, and Apollo then quietly re-fetches them from the network. On a screen
 * that shows a loading indicator whenever one of its queries is loading, the already
 * visible content flips back to the loading state, which makes snapshot tests fail at
 * random on the slower CI machines.
 */
const simpleMerge: FieldMergeFunction = (existing, incoming, { mergeObjects }) => {
    return mergeObjects(existing, incoming);
};

const chatMessagesPagination: FieldPolicy = {
    keyArgs: false,
    merge(existing, incoming, { args }) {
        if (!existing) {
            return incoming;
        }

        const existingEdges = existing.edges ?? [];
        const incomingEdges = incoming.edges ?? [];
        const getId = (e: CacheEdge) => e?.node?.__ref ?? e?.node?.id;

        if (!args?.after) {
            // Subscription update or refetch (no cursor) — merge incoming edges
            // with existing, keeping older paginated edges that aren't in incoming
            const incomingIds = new Set(incomingEdges.map(getId));
            const keptExistingEdges = existingEdges.filter((e: CacheEdge) => !incomingIds.has(getId(e)));

            return {
                ...incoming,
                edges: [...incomingEdges, ...keptExistingEdges],
                pageInfo: {
                    ...incoming.pageInfo,
                    hasNextPage: existing.pageInfo?.hasNextPage ?? incoming.pageInfo?.hasNextPage,
                    endCursor: existing.pageInfo?.endCursor ?? incoming.pageInfo?.endCursor
                }
            };
        }

        // fetchMore: deduplicate and append incoming edges
        const existingIds = new Set(existingEdges.map(getId));
        const newEdges = incomingEdges.filter((e: CacheEdge) => !existingIds.has(getId(e)));

        return {
            ...incoming,
            edges: [...existingEdges, ...newEdges],
            pageInfo: incoming.pageInfo
        };
    },
    read(existing) {
        return existing;
    }
};

export const cacheConfig: InMemoryCacheConfig = {
    possibleTypes: {
        ChatAttachment: ['ChatMessageAttachmentNode', 'ChatMessageIdAttachmentNode', 'ChatMessageInteractionNode']
    },
    typePolicies: {
        Query: {
            fields: {
                // See the note above simpleMerge before changing or removing this.
                root: {
                    merge: simpleMerge
                }
            }
        },
        ChatNode: {
            fields: {
                messages: chatMessagesPagination,
                messageInteractions: {
                    merge: false
                },
                notifications: {
                    merge: false
                }
            }
        },
        ChatMessageNode: {
            fields: {
                attachmentsV2: {
                    merge: false
                }
            }
        },
        // Temporary fix to 'missing field chat' error from apollo when receiving subscription confirmation
        // message until https://github.com/apollographql/apollo-client/issues/8677 is fixed.
        Subscription: {
            fields: {
                chat: {
                    read(chat) {
                        return chat ?? null;
                    }
                }
            }
        },
        CalendarReservationAccessNode: {
            merge: simpleMerge,
            fields: {
                appointmentDays: appointmentDaysTypePolicy
            }
        }
    }
};
